import React, { FC } from 'react';
import styled from 'styled-components';
import { SystemNote, SystemNoteType } from '../../models';
import CloseIcon from '@material-ui/icons/Close';
import ErrorIcon from '@material-ui/icons/Error';
import WarningIcon from '@material-ui/icons/Warning';
import InfoIcon from '@material-ui/icons/Info';
import { IconButton } from '@material-ui/core';

// Defined early so renderText can reference it before the full styled block.
const NoteParagraph = styled.p<{ children?: React.ReactNode }>`
    margin: 0 0 var(--spacer-S) 0;
    line-height: 1.5;

    &:last-child {
        margin-bottom: 0;
    }
`;

// ─── Inline markdown renderer ───────────────────────────────────────────────
// Supports **bold**, _italic_, __underline__, [label](https://url)
// Each \n-separated line is rendered as its own <p> for proper paragraph spacing.
const INLINE_PATTERN = /\*\*(.+?)\*\*|__(.+?)__|_(.+?)_|\[(.+?)\]\((https?:\/\/[^\s)]+)\)/g;

function renderLine(line: string, keyOffset: number): { nodes: React.ReactNode[]; nextKey: number } {
    const nodes: React.ReactNode[] = [];
    let key = keyOffset;

    INLINE_PATTERN.lastIndex = 0;
    let lineLastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = INLINE_PATTERN.exec(line)) !== null) {
        if (match.index > lineLastIndex) nodes.push(line.slice(lineLastIndex, match.index));

        if      (match[1] !== undefined) nodes.push(<strong key={key++}>{match[1]}</strong>);
        else if (match[2] !== undefined) nodes.push(<u key={key++}>{match[2]}</u>);
        else if (match[3] !== undefined) nodes.push(<em key={key++}>{match[3]}</em>);
        else if (match[4] !== undefined) nodes.push(<a key={key++} href={match[5]} target="_blank" rel="noopener noreferrer">{match[4]}</a>);

        lineLastIndex = INLINE_PATTERN.lastIndex;
    }

    if (lineLastIndex < line.length) nodes.push(line.slice(lineLastIndex));

    return { nodes, nextKey: key };
}

function renderText(text: string): React.ReactNode[] {
    const paragraphs: React.ReactNode[] = [];
    let key = 0;

    text.split('\n').forEach((line, i) => {
        const { nodes, nextKey } = renderLine(line, key);
        key = nextKey;
        paragraphs.push(<NoteParagraph key={`p-${i}`}>{nodes}</NoteParagraph>);
    });

    return paragraphs;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
const NoteTypeIcon: FC<{ type: SystemNoteType }> = ({ type }) => {
    if (type === SystemNoteType.Critical) return <ErrorIcon fontSize="small" />;
    if (type === SystemNoteType.Warning)  return <WarningIcon fontSize="small" />;
    return <InfoIcon fontSize="small" />;
};

const borderColor = (type: SystemNoteType): string => {
    if (type === SystemNoteType.Critical) return 'var(--danger-color)';
    if (type === SystemNoteType.Warning)  return 'var(--warning-color)';
    return 'var(--info-color)';
};

const bgColor = (type: SystemNoteType): string => {
    if (type === SystemNoteType.Critical) return 'rgba(220, 53, 69, 0.07)';
    if (type === SystemNoteType.Warning)  return 'var(--warning-bg)';
    return 'var(--bg-hover-light)';
};

const labelColor = (type: SystemNoteType): string => {
    if (type === SystemNoteType.Critical) return 'var(--danger-color)';
    if (type === SystemNoteType.Warning)  return '#856404';
    return 'var(--info-color)';
};

// ─── Styled components ────────────────────────────────────────────────────────
const BannerWrapper = styled.div`
    position: fixed;
    top: var(--spacer-S);
    left: var(--spacer-M);
    right: var(--spacer-M);
    max-width: 1140px;
    margin: 0 auto;
    z-index: 1200;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    overflow: hidden;
`;

const NoteRow = styled.div<{ noteType: SystemNoteType; children?: React.ReactNode }>`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--spacer-S);
    padding: var(--spacer-S) var(--spacer-M);
    border-left: var(--border-thick) solid ${({ noteType }) => borderColor(noteType)};
    background-color: ${({ noteType }) => bgColor(noteType)};

    & + & {
        border-top: var(--border-thin) solid ${({ noteType }) => borderColor(noteType)};
    }
`;

const NoteContent = styled.div`
    flex: 1;
    min-width: 0;
`;

const NoteHeader = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-XS);
    margin-bottom: var(--spacer-S);
`;

const NoteIconWrapper = styled.div<{ noteType: SystemNoteType; children?: React.ReactNode }>`
    flex-shrink: 0;
    display: flex;
    color: ${({ noteType }) => labelColor(noteType)};
`;

const NoteTitle = styled.div`
    font-weight: 600;
    font-size: 0.95rem;
    color: var(--text-body);
`;

const NoteText = styled.div`
    font-size: 0.9rem;
    color: var(--text-body);

    a {
        color: var(--appBlue);
        text-decoration: underline;
    }
`;

const NoteImageWrapper = styled.div`
    flex-shrink: 0;
    align-self: center;

    img {
        display: block;
        max-width: 200px;
        max-height: 130px;
        border-radius: 4px;
        object-fit: cover;
    }

    @media (max-width: 576px) {
        order: 1;
        width: 100%;

        img {
            max-width: 100%;
            max-height: 180px;
        }
    }
`;

const CloseButton = styled(IconButton)`
    flex-shrink: 0;
    padding: 4px !important;
    align-self: flex-start;
    opacity: 0.6;

    &:hover {
        opacity: 1;
    }
`;

// ─── Component ────────────────────────────────────────────────────────────────
interface Props {
    notes: SystemNote[];
    onClose: () => void;
}

export const SystemNotesBanner: FC<Props> = ({ notes, onClose }) => {
    if (notes.length === 0) return null;

    return (
        <BannerWrapper>
            {notes.map((note, i) => (
                <NoteRow key={i} noteType={note.type}>
                    <NoteContent>
                        <NoteHeader>
                            <NoteIconWrapper noteType={note.type}>
                                <NoteTypeIcon type={note.type} />
                            </NoteIconWrapper>
                            {note.title && <NoteTitle>{note.title}</NoteTitle>}
                        </NoteHeader>
                        <NoteText>{renderText(note.text)}</NoteText>
                    </NoteContent>
                    {note.imageUrl && <NoteImageWrapper><img src={note.imageUrl} alt="" /></NoteImageWrapper>}
                    {i === 0 && (
                        <CloseButton size="small" onClick={onClose} aria-label="Dismiss">
                            <CloseIcon fontSize="small" />
                        </CloseButton>
                    )}
                </NoteRow>
            ))}
        </BannerWrapper>
    );
};
